import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  companyApiClient,
  CompanyShowResponse,
  CompanyUpdateErrorResponse,
} from '@src/api/tenant/company/companyApiClient';
import { closingMonth } from '@src/components/closingMonth';
import { employeeScales } from '@src/components/employeeScales';
import { industries } from '@src/components/industries';
import { prefectures } from '@src/components/prefectures';
import { revenueScales } from '@src/components/revenueScales';
import { EnableStatus } from '@src/enum/EnableStatus.ts';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant.tsx';
import Button from '@src/ui/elements/button/Button';
import ErrorMessage from '@src/ui/elements/form/ErrorMessage';
import Input from '@src/ui/elements/form/Input';
import Select, { SelectorItem } from '@src/ui/elements/form/Select';
import Textarea from '@src/ui/elements/form/Textarea';
import Title from '@src/ui/elements/typography/Title.tsx';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import React, { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

type FormValues = {
  name: string;
  nameKana: string;
  industry: string;
  enabled: string;
  postalCodeFirst: string;
  postalCodeSecond: string;
  prefectureCode: string;
  address: string;
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  birthday: string;
  establishmentYear: string;
  fiscalMonth: string;
  serviceDescription: string;
  revenusScale: string;
  employeeScale: string;
  approvalStatus: string;
};
type StatusSelector = SelectorItem[];
const CompanyEditForm: React.FC = () => {
  const [company, setCompany] = useState<CompanyShowResponse>();
  const { id } = useParams<{ id: string }>();
  const [errors, setErrors] = useState<CompanyUpdateErrorResponse>();
  const toast = useToast();
  const [statusSelector, setStatusSelector] = useState<StatusSelector>();
  const navigate = useNavigate();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [formValues, setFormValues] = useState<FormValues | null>(null);

  const fetchCompany = useCallback(async () => {
    const res = await companyApiClient.show(Number(id));
    handleApiResponse(res, (data) => {
      setCompany(data);
      const initialFormValues: FormValues = {
        name: data.name,
        nameKana: data.name_kana,
        enabled: data.enabled ? 'ENABLED' : 'DISABLED',
        industry: data.industry,
        postalCodeFirst: data.postal_code.slice(0, 3),
        postalCodeSecond: data.postal_code.slice(3),
        prefectureCode: data.prefecture_code,
        address: data.address,
        familyName: data.family_name,
        firstName: data.first_name,
        familyNameKana: data.family_name_kana,
        firstNameKana: data.first_name_kana,
        birthday: data.birthday,
        establishmentYear: data.establishment_year ? data.establishment_year.toString() : '',
        fiscalMonth: data.fiscal_month ? data.fiscal_month.toString() : '',
        serviceDescription: data.service_description ? data.service_description : '',
        revenusScale: data.revenus_scale.toString(),
        employeeScale: data.employee_scale.toString(),
        approvalStatus: data.approval_status,
      };
      setFormValues(initialFormValues);
      setStatusSelector([
        { id: 'ENABLED', name: EnableStatus.ENABLED },
        { id: 'DISABLED', name: EnableStatus.DISABLED },
      ]);
    });
  }, [id]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const update = async () => {
    setErrors(undefined);
    if (!formValues) {
      return;
    }

    const formatPostalCode = () => {
      if (!formValues.postalCodeFirst && !formValues.postalCodeSecond) {
        return '';
      }
      return `${formValues.postalCodeFirst}-${formValues.postalCodeSecond}`;
    };

    const response = await companyApiClient.update(Number(id), {
      company: {
        name: formValues.name,
        name_kana: formValues.nameKana,
        enabled: formValues?.enabled ? formValues?.enabled === 'ENABLED' : undefined,
        postal_code: formatPostalCode(),
        prefecture_code: formValues.prefectureCode,
        address: formValues.address,
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        birthday: formValues.birthday,
        establishment_year: formValues.establishmentYear,
        fiscal_month: formValues.fiscalMonth,
        industry: formValues.industry,
        service_description: formValues.serviceDescription,
        revenus_scale: formValues.revenusScale,
        employee_scale: formValues.employeeScale,
      },
    });
    handleApiResponse(
      response,
      () => {
        toast(TOAST_MESSAGE.UPDATE);
        fetchCompany();
        navigate(TENANT_URL.COMPANIES);
      },
      (errors) => {
        setErrors(errors);
      },
    );
  };

  const handleClickDelete = async () => {
    setIsOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const res = await companyApiClient.delete(Number(id));
    handleApiResponse(res, () => {
      toast(TOAST_MESSAGE.DELETE);
      setIsOpenDeleteModal(false);
      navigate(TENANT_URL.COMPANIES);
    });
  };

  const handleCancelDelete = () => {
    setIsOpenDeleteModal(false);
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className='px-6 pb-14 pt-2'>
      <div>
        <div className='flex justify-between gap-4'>
          <div className='w-full'>
            <Title>{company?.name}</Title>
          </div>
          <div className='flex-[0_0_auto] text-sm text-warning underline hover:no-underline'>
            <button
              type='button'
              className='text-sm text-warning-500 underline'
              onClick={handleClickDelete}
            >
              企業を削除する
            </button>
          </div>
        </div>
        {formValues && (
          <div className='m-auto w-4/5'>
            <form
              className='mt-[60px] flex flex-col gap-[26px]'
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>企業名</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='サンプル株式会社'
                      value={formValues?.name || ''}
                      onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                      error={hasError(errors?.company.name)}
                    />
                  </div>
                  <ErrorMessage>{firstError(errors?.company.name)}</ErrorMessage>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>企業名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='サンプルカブシキガイシャ'
                      value={formValues?.nameKana || ''}
                      onChange={(e) => setFormValues({ ...formValues, nameKana: e.target.value })}
                      error={hasError(errors?.company.name_kana)}
                    />
                  </div>
                  <ErrorMessage>{firstError(errors?.company.name_kana)}</ErrorMessage>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>本社所在地</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-4'>
                  <div className='flex flex-col gap-[6px]'>
                    <div
                      className='flex items-center gap-[6px]'
                      ref={(el) => (fieldRefs.current['company.postal_code'] = el)}
                    >
                      <span>〒</span>
                      <div className='w-[100px]'>
                        <Input
                          size='m'
                          type='text'
                          placeholder='123'
                          value={formValues?.postalCodeFirst || ''}
                          onChange={(e) =>
                            setFormValues({ ...formValues, postalCodeFirst: e.target.value })
                          }
                          error={hasError(errors?.company.postal_code)}
                        />
                      </div>
                      <span>-</span>
                      <div className='w-[100px]'>
                        <Input
                          size='m'
                          type='text'
                          placeholder='4567'
                          value={formValues?.postalCodeSecond || ''}
                          onChange={(e) =>
                            setFormValues({ ...formValues, postalCodeSecond: e.target.value })
                          }
                          error={hasError(errors?.company.postal_code)}
                        />
                      </div>
                    </div>
                    <ErrorMessage>{firstError(errors?.company.postal_code)}</ErrorMessage>
                  </div>
                  <div className='flex flex-col gap-[6px]'>
                    <div className='flex gap-[6px]'>
                      <div
                        className='flex w-[164px] flex-[0_0_auto] flex-col gap-[6px]'
                        ref={(el) => (fieldRefs.current['company.prefecture_code'] = el)}
                      >
                        <Select
                          items={prefectures}
                          size='m'
                          initialValue={formValues?.prefectureCode || ''}
                          onSelect={(value) =>
                            setFormValues({ ...formValues, prefectureCode: value })
                          }
                          error={hasError(errors?.company.prefecture_code)}
                        />
                        <ErrorMessage>{firstError(errors?.company.prefecture_code)}</ErrorMessage>
                      </div>
                      <div
                        className='flex w-full flex-col gap-[6px]'
                        ref={(el) => (fieldRefs.current['company.address'] = el)}
                      >
                        <Input
                          size='m'
                          type='text'
                          placeholder='〇〇市〇〇区1-2-3'
                          value={formValues?.address || ''}
                          onChange={(e) =>
                            setFormValues({ ...formValues, address: e.target.value })
                          }
                          error={hasError(errors?.company.address)}
                        />
                        <ErrorMessage>{firstError(errors?.company.address)}</ErrorMessage>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>代表者名</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex w-full flex-col gap-[6px]'>
                  <div className='flex gap-4'>
                    <div
                      className='flex w-full flex-col'
                      ref={(el) => (fieldRefs.current['company.family_name'] = el)}
                    >
                      <Input
                        size='m'
                        type='text'
                        placeholder='山田'
                        value={formValues?.familyName || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, familyName: e.target.value })
                        }
                        error={hasError(errors?.company.family_name)}
                      />
                      <ErrorMessage>{firstError(errors?.company.family_name)}</ErrorMessage>
                    </div>
                    <div
                      className='flex w-full flex-col'
                      ref={(el) => (fieldRefs.current['company.first_name'] = el)}
                    >
                      <Input
                        size='m'
                        type='text'
                        placeholder='太郎'
                        value={formValues?.firstName || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, firstName: e.target.value })
                        }
                        error={hasError(errors?.company.first_name)}
                      />
                      <ErrorMessage>{firstError(errors?.company.first_name)}</ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>代表者名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex w-full flex-col gap-[6px]'>
                  <div className='flex gap-4'>
                    <div
                      className='flex w-full flex-col'
                      ref={(el) => (fieldRefs.current['company.family_name_kana'] = el)}
                    >
                      <Input
                        size='m'
                        type='text'
                        placeholder='ヤマダ'
                        value={formValues?.familyNameKana || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, familyNameKana: e.target.value })
                        }
                        error={hasError(errors?.company.family_name_kana)}
                      />
                      <ErrorMessage>{firstError(errors?.company.family_name_kana)}</ErrorMessage>
                    </div>
                    <div
                      className='flex w-full flex-col'
                      ref={(el) => (fieldRefs.current['company.first_name_kana'] = el)}
                    >
                      <Input
                        size='m'
                        type='text'
                        placeholder='タロウ'
                        value={formValues?.firstNameKana || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, firstNameKana: e.target.value })
                        }
                        error={hasError(errors?.company.first_name_kana)}
                      />
                      <ErrorMessage>{firstError(errors?.company.first_name_kana)}</ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>代表者生年月日</label>
                </div>
                <div className='flex h-[50px] gap-4'>
                  <div
                    className='flex w-[200px] flex-col'
                    ref={(el) => (fieldRefs.current['company.birthday'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='19900101'
                      value={formValues?.birthday}
                      onChange={(e) => setFormValues({ ...formValues, birthday: e.target.value })}
                      error={hasError(errors?.company.birthday)}
                      inputMode='numeric'
                    />
                    <ErrorMessage>{firstError(errors?.company.birthday)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>設立年</label>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div className='flex w-full flex-col'>
                    <div className='flex items-center gap-[6px]'>
                      <div
                        className='w-[200px]'
                        ref={(el) => (fieldRefs.current['company.establishment_year'] = el)}
                      >
                        <Input
                          size='m'
                          type='text'
                          placeholder='2014'
                          value={formValues?.establishmentYear || ''}
                          onChange={(e) =>
                            setFormValues({ ...formValues, establishmentYear: e.target.value })
                          }
                          error={hasError(errors?.company.establishment_year)}
                        />
                      </div>
                      <span>年</span>
                    </div>
                  </div>
                  <ErrorMessage>{firstError(errors?.company.establishment_year)}</ErrorMessage>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>業種</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.industry'] = el)}
                  >
                    <Select
                      items={industries}
                      size='m'
                      initialValue={formValues?.industry || ''}
                      onSelect={(value) => setFormValues({ ...formValues, industry: value })}
                      error={hasError(errors?.company.industry)}
                    />
                    <ErrorMessage>{firstError(errors?.company.industry)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>提供商品・サービス</label>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex h-[120px] w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.service_description'] = el)}
                  >
                    <Textarea
                      error={hasError(errors?.company.service_description)}
                      value={formValues?.serviceDescription || ''}
                      placeholder='例：AIを活用したデータ分析サービスや、企業向けクラウドサービスの提供'
                      onChange={(e) =>
                        setFormValues({ ...formValues, serviceDescription: e.target.value })
                      }
                    />
                    <ErrorMessage>{firstError(errors?.company.service_description)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>売上規模</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.revenus_scale'] = el)}
                  >
                    <Select
                      items={revenueScales}
                      size='m'
                      initialValue={formValues?.revenusScale || ''}
                      onSelect={(value) => setFormValues({ ...formValues, revenusScale: value })}
                      error={hasError(errors?.company.revenus_scale)}
                    />
                    <ErrorMessage>{firstError(errors?.company.revenus_scale)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>従業員数規模</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.employee_scale'] = el)}
                  >
                    <Select
                      items={employeeScales}
                      size='m'
                      initialValue={formValues?.employeeScale || ''}
                      onSelect={(value) => setFormValues({ ...formValues, employeeScale: value })}
                      error={hasError(errors?.company.employee_scale)}
                    />
                    <ErrorMessage>{firstError(errors?.company.employee_scale)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>決算月</label>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.fiscal_month'] = el)}
                  >
                    <Select
                      items={closingMonth}
                      size='m'
                      initialValue={formValues?.fiscalMonth || ''}
                      onSelect={(value) => setFormValues({ ...formValues, fiscalMonth: value })}
                      error={hasError(errors?.company.fiscal_month)}
                    />
                    <ErrorMessage>{firstError(errors?.company.fiscal_month)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>ステータス</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div className='flex w-full flex-col'>
                    <div
                      ref={(el) => (fieldRefs.current['company.enabled'] = el)}
                      className='flex w-full flex-col'
                    >
                      {statusSelector && (
                        <Select
                          items={statusSelector}
                          size='m'
                          initialValue={formValues?.enabled || ''}
                          onSelect={(value) => setFormValues({ ...formValues, enabled: value })}
                          error={hasError(errors?.company.enabled)}
                        />
                      )}
                    </div>
                    <ErrorMessage>{firstError(errors?.company.enabled)}</ErrorMessage>
                  </div>
                </div>
              </div>
              {formValues?.approvalStatus && (
                <div className='flex flex-col gap-[6px]'>
                  <div className='flex items-center gap-3'>
                    <label className=''>承認状況</label>
                  </div>
                  <div className='flex flex-col gap-[6px]'>
                    <div className='flex w-full flex-col'>
                      <div className='flex w-full flex-col'>{formValues?.approvalStatus}</div>
                    </div>
                  </div>
                </div>
              )}
              <div className='mt-auto flex justify-center gap-5 pt-4'>
                <div className='w-[172px]'>
                  <Button
                    variant='secondary'
                    fullRounded
                    fullWidth
                    outline
                    onClick={() => navigate(generatePath(TENANT_URL.COMPANIES))}
                  >
                    キャンセル
                  </Button>
                </div>
                <div className='w-[172px]'>
                  <Button
                    fullRounded
                    fullWidth
                    outline
                    onClick={update}
                  >
                    保存する
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
      <ConfirmModal
        title='完全に削除しますか？'
        message='企業が完全に削除されますがよろしいですか？'
        confirmText='完全に削除'
        cancelText='戻る'
        isOpen={isOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        isDanger={true}
      />
    </main>
  );
};

export default CompanyEditForm;
